export type TruncateBy = 'chars' | 'words';

export interface TruncateOptions {
    limit?: number;
    by?: TruncateBy;
    /** true -> "…", false -> "", or a custom string like "..." */
    ellipsis?: boolean | string;
}

interface NormalizedOptions {
    limit: number;
    by: TruncateBy;
    ellipsisString: string;
}

/** Detects the direct-call form: useTruncate(text, options) */
function isDirectTextCall(
    possibleText: unknown
): possibleText is string | number | null | undefined {
    return (
        typeof possibleText === 'string' ||
        typeof possibleText === 'number' ||
        possibleText == null
    );
}

/** Normalize options with defaults */
function normalizeOptions(incomingOptions?: TruncateOptions): NormalizedOptions {
    const normalizedLimit =
    typeof incomingOptions?.limit === 'number'
        ? Math.max(0, incomingOptions.limit)
        : 140;
    
    const normalizedBy: TruncateBy =
    incomingOptions?.by === 'words' ? 'words' : 'chars';
    
    const normalizedEllipsisString =
    incomingOptions?.ellipsis === false
        ? ''
        : incomingOptions?.ellipsis === true || incomingOptions?.ellipsis === undefined
            ? '…'
            : String(incomingOptions.ellipsis);
    
    return {
        limit: normalizedLimit,
        by: normalizedBy,
        ellipsisString: normalizedEllipsisString,
    };
}

/** Very simple HTML tag stripper with basic entity decoding */
function stripTagsSimple(inputHtml: string): string {
    if (!inputHtml) return '';
    
    // Remove comments, script and style blocks
    const withoutComments = inputHtml.replace(/<!--[\s\S]*?-->/g, ' ');
    const withoutScript = withoutComments.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
    const withoutStyle = withoutScript.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
    
    // Remove all remaining tags
    const withoutTags = withoutStyle.replace(/<[^>]+>/g, ' ');
    
    // Decode a few common HTML entities and normalize whitespace
    const decoded = withoutTags
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    
    return decoded.replace(/\s+/g, ' ').trim();
}

/** Split a string into grapheme clusters so emojis do not get cut in half */
function splitIntoGraphemes(inputString: string): string[] {
    const anyIntl: any = Intl as any;
    if (anyIntl && typeof anyIntl.Segmenter === 'function') {
        const segmenter = new anyIntl.Segmenter(undefined, { granularity: 'grapheme' });
        return Array.from(segmenter.segment(inputString), (info: any) => info.segment);
    }
    return Array.from(inputString);
}

/** Truncate plain text by characters or words */
function truncatePlainText(plainText: string, options: NormalizedOptions): string {
    if (!plainText || options.limit === 0) return '';
    
    if (options.by === 'words') {
        const words = plainText.split(/\s+/); // whitespace already normalized
        if (words.length <= options.limit) return plainText;
        const limited = words.slice(0, options.limit).join(' ');
        return limited + options.ellipsisString;
    }
    
    // by === 'chars'
    const graphemes = splitIntoGraphemes(plainText);
    if (graphemes.length <= options.limit) return plainText;
    return graphemes.slice(0, options.limit).join('') + options.ellipsisString;
}

/** Core worker: strip tags then truncate */
function stripAndTruncate(rawInput: string, options: NormalizedOptions): string {
    const plainText = stripTagsSimple(rawInput);
    return truncatePlainText(plainText, options);
}

/**
* useTruncate
*
* Overload 1 - direct: useTruncate(text, options) -> string
* Overload 2 - factory: useTruncate(options) -> (text, overrides?) => string
*/
export function useTruncate(
    inputText: string | number | null | undefined,
    truncateOptions?: TruncateOptions
): string;
export function useTruncate(
    presetTruncateOptions?: TruncateOptions
): (
    inputText: string | number | null | undefined,
    overrideOptions?: TruncateOptions
) => string;

export function useTruncate(
    firstParameter?: unknown,
    secondParameter?: TruncateOptions
): unknown {
    const runOnce = (
        rawInputText: string | number | null | undefined,
        providedOptions?: TruncateOptions
    ): string => {
        const normalizedText = rawInputText == null ? '' : String(rawInputText);
        const normalizedOptions = normalizeOptions(providedOptions);
        return stripAndTruncate(normalizedText, normalizedOptions);
    };
    
    // Direct call: useTruncate(text, options)
    if (isDirectTextCall(firstParameter)) {
        return runOnce(firstParameter, secondParameter);
    }
    
    // Factory call: useTruncate(options) -> (text, overrides?) => string
    const presetOptions = (firstParameter as TruncateOptions | undefined) ?? {};
    return (
        input: string | number | null | undefined,
        overrideOptions?: TruncateOptions
    ) => runOnce(input, { ...presetOptions, ...(overrideOptions ?? {}) });
}